import {
  TEST_TENANT_REGISTRY,
  isRegisteredTestTenant,
  isTestTenant,
} from '@etiquetachef/types';

/**
 * Filtro de tenant de teste dos painéis do admin: tudo que RELATA (dashboard,
 * ranking, total) passa por aqui para tirar os tenants de teste da conta.
 *
 * Origem: #1008 — 2026-10-19 20:57 +0200 (auditoria da #1006: os painéis do
 * admin que leem tenants/billing/uso crus ainda contavam teste como cliente).
 *
 * Por quê: desde a #1006 "é teste" tem uma fonte só — `isTestTenant` /
 * `isRegisteredTestTenant` de `@etiquetachef/types` (cadastro `testTenants.ts`
 * OU assinatura `livemode === false`). Este módulo só ADAPTA essa fonte às
 * formas que os hooks do admin têm em mãos (assinatura parseada com
 * `livemode` nullish, só o id, lista de pares); nunca guarda lista de ids
 * própria. Decisão do owner (2026-10-19, #1008): o volume do simulador de uso
 * (IT-98765432103, BR-01010001000180) sai de TODOS os painéis, inclusive os de
 * uso — ele existe para os tenants de teste parecerem cozinha real, não para
 * medir clientes.
 *
 * Contrato: funções puras, sem I/O.
 *  - `isReportExcludedTenant(id, sub)` — `true` = fora do relatório. `sub`
 *    `null`/ausente = só o cadastro decide (o hook não leu a assinatura).
 *  - `withoutTestTenantEntries(entries)` — filtra pares `[tenantId, valor]`
 *    (mapas de totais por tenant) só pelo cadastro.
 *  - `registeredTestTenantIds()` — os ids do cadastro, para o hook que precisa
 *    SUBTRAIR o volume deles de um agregado que não filtra por tenant.
 *
 * Usado por (todos web `apps/admin`, super-admin): `dashboard-sales/useSalesMetrics.ts`,
 * `dashboard-churn/useChurnedTenants.ts`, `dashboard-customers/useCustomers.ts`,
 * `dashboard-usage-basic/useUsageBasic.ts`, `dashboard-usage/useUsage.ts`,
 * `dashboard-usage/useAllTimePrintedGlobal.ts`, `dashboard-product/useProduct.ts`,
 * `dashboard-finance/LifetimeFreeCard.tsx`.
 *
 * Arquitetura: regra RELATAR × OPERAR (#1006) — só relatório usa isto. Tela
 * operacional (lista de Empresas, lista de promoções, detalhe do tenant)
 * continua mostrando o tenant de teste, com o status "Teste". Agregado gravado
 * pelas Functions (`aggregations/**`) é filtrado na Function que o escreve,
 * nunca aqui — o admin só vê o número pronto (#1008, item 1–6).
 *
 * Falhas conhecidas: o hook que só tem o id (uso, produto, país) não vê o
 * `livemode`: um tenant sandbox criado por e2e e NÃO cadastrado continua
 * contando lá. Aceito na #1008 — esses tenants quase não imprimem, e ler a
 * assinatura de todo tenant (ou um índice CG novo em `billing.livemode`)
 * custaria mais que o desvio. O simulador, que é o volume que distorce, está
 * no cadastro.
 *
 * Dicas: tenant de teste novo? Cadastre em `packages/types/src/testTenants.ts`
 * — todos os painéis passam a excluí-lo sem mexer aqui.
 *
 * Issues: #1008 (origem), #1006 (fonte única do "é teste").
 *
 * Histórico:
 *  - 2026-10-19 20:57 +0200 — (#1008) criado.
 */
export function isReportExcludedTenant(
  tenantId: string,
  subscription?: { readonly livemode?: boolean | null | undefined } | null,
): boolean {
  if (subscription === null || subscription === undefined) return isRegisteredTestTenant(tenantId);
  // `livemode: null` (doc legado) = ausente = live, como em `isTestModeRecord`.
  return isTestTenant(tenantId, { livemode: subscription.livemode ?? undefined });
}

/** Pares `[tenantId, valor]` sem os tenants de teste do cadastro (#1008). */
export function withoutTestTenantEntries<V>(
  entries: Iterable<readonly [string, V]>,
): [string, V][] {
  const out: [string, V][] = [];
  for (const [id, v] of entries) {
    if (!isRegisteredTestTenant(id)) out.push([id, v]);
  }
  return out;
}

/**
 * Ids do cadastro de teste. O cadastro contém as listas do simulador e do
 * billing sandbox (`testTenants.test.ts` cobra), então é a lista completa.
 */
export function registeredTestTenantIds(): readonly string[] {
  return Object.keys(TEST_TENANT_REGISTRY);
}
